import {useEffect, useMemo, useState} from 'react'
import useQueryCopyDashboard from '../../hooks/api/analysis/useQueryCopyDashboard'
import moment from 'moment'
import {ICopyDashboard} from '../../apis/analysis/getCopyDashboard'
import {Tooltip, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis} from 'recharts'
import Text from '../../components/text/Text'
import ContainerLayout from '../../components/layout/ContainerLayout'
import PageLayout from '../../components/layout/PageLayout'
import WizSelectButton from '../../components/button/WizSelectButton'
import WizSwitchButton from '../../components/button/WizSwitchButton'
import WizSelect from '../../components/select/WizSelect'
import {getValueFromId} from '../../components/select/constSelect'

interface IProps {
    className?: string
}

interface IChartData extends Omit<ICopyDashboard, 'day'> {
    date?: string
}

const CopyDashboard: React.FC<IProps> = ({className}) => {
    const [limit, setLimit] = useState(100)
    const [dataType, setDataType] = useState('daily')
    const {data, refetch} = useQueryCopyDashboard(limit, dataType)

    const chartDatas = [
        {label: 'Registered Leaders(key)', dataKey: 'r_leader_count'},
        {label: 'Active Leaders(key)', dataKey: 'leader_count'},
        {label: 'Active Copiers(key)', dataKey: 'copier_count'},
        {label: 'Copiers’ total Balance ($)', dataKey: 'managed_asset'},
        {label: 'Total Daily Earning ($) from Copy Trading', dataKey: 'earned'},
        {label: 'Acumulate Daily Earning ($) from Copy Trading', dataKey: 'accumulated_earned'},
        {label: 'Average Earning (Total Daily Earning/No. of Copiers)', dataKey: 'avg_earned'},
        {label: 'Total Daily Profit (%) from Copy Trading', dataKey: 'profit'},
        {label: 'Total Daily Volume from Copy Trading', dataKey: 'trade_volume'},
        {label: 'Total Daily Trade Count', dataKey: 'trade_count'},
        {label: 'Volume per Trade (Daily Volume/Daily Trade Count)', dataKey: 'average'},
        {label: 'Median Volume (Median among Copy Traders)', dataKey: 'median'},
    ]

    const rgbColors = [
        'rgb(251, 206, 177)',
        'rgb(219, 112, 147)',
        'rgb(255, 68, 51)',
        'rgb(244, 180, 0)',
        'rgb(248, 108, 108)',
        'rgb(50, 23, 77)',
        'rgb(50, 74, 178)',
        'rgb(72, 60, 50)',
        'rgb(0, 73, 88)',
        'rgb(0, 144, 0)',
    ]

    const chart = useMemo<IChartData[]>(() => {
        if (data === undefined) {
            return []
        }

        const res = data.map<IChartData>(el => {
            let temp = {}
            Object.keys(el).forEach(k => {
                if (typeof el[k] === 'number') {
                    temp[k] = parseFloat(el[k].toFixed(3))
                }
            })

            return {
                ...el,
                ...temp,
                date: moment(new Date(el.day).getTime() * 1000).format('YYYY/MM/DD'),
            }
        })

        return res
    }, [data])

    useEffect(() => {
        refetch()
    }, [data, limit, dataType])

    return (
        <PageLayout title="Copy Trading Dashboard">
            <div className={'flex space-x-[10px]'}>
                <WizSelect optionKey={'graph_limit'} value={limit} onChange={e => setLimit(e as number)} />
                <WizSelect optionKey={'copy_data_type'} value={dataType} onChange={e => setDataType(e as string)} />
            </div>
            <div className="grid grid-cols-2 auto-cols-max gap-2 w-full h-full ">
                {chartDatas.map(el => {
                    return (
                        <ContainerLayout title={el.label}>
                            <ResponsiveContainer width="100%" height={400}>
                                <ComposedChart
                                    syncId={'cd'}
                                    margin={{top: 5, right: 30, left: 20, bottom: 5}}
                                    data={chart}>
                                    <XAxis dataKey="date" />
                                    <YAxis orientation="left" />
                                    <Legend />
                                    <Tooltip />
                                    <Line
                                        dot={<div></div>}
                                        type="monotone"
                                        dataKey={el.dataKey}
                                        stroke={rgbColors[Math.floor(Math.random() * rgbColors.length)]}
                                        strokeWidth={1}
                                        activeDot={{r: 3}}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </ContainerLayout>
                    )
                })}
            </div>
        </PageLayout>
    )
}

export default CopyDashboard
